import { fetchFile } from './client';
import { query, type Cursor, type CursorPage } from './paging';

/** = the backend's audit categories (GET /audit?category=, comma-separated for several). */
export type AuditCategory = 'account' | 'session' | 'user' | 'location' | 'gateway' | 'cylinder' | 'shipment' | 'alert' | 'ota' | 'system';
/**
 * Who did it: a signed-in user, a maintenance command (CLI), the CI release upload, a stranger at the sign-in, an
 * account deleted since (`userEmail` still says whose), or the server.
 */
export type AuditSource = 'user' | 'cli' | 'ci' | 'anonymous' | 'deleted' | 'system';
/** Objects the dashboard has a page for. */
export type AuditRefType = 'location' | 'cylinder' | 'gateway' | 'shipment' | 'user' | 'alert';

/** One entry as GET /audit lists it (the backend's AuditItem). */
export interface AuditEntry {
  id: number;
  at: string;
  action: string;
  category: AuditCategory | null;
  target: string | null;
  /** person-readable name of the target (location name, Surat Jalan number, gateway label, user name) */
  targetLabel: string | null;
  /** the object's page, when it still exists */
  targetRef: { type: AuditRefType; id: string } | null;
  userId: number | null;
  userName: string | null;
  userEmail: string | null;
  source: AuditSource;
  /** camelCase keys; entries written before the 2026-10 refactor still carry snake_case ones (see auditDetail) */
  detail: Record<string, unknown>;
}

export type AuditPage = CursorPage<AuditEntry>;

/** `userId` filter value for entries without a signed-in user (CLI, CI, the server). */
export const AUDIT_SYSTEM_USER = 'system';

/** The filters as the API takes them (empty strings are left out). `userId` is an id or "system". */
export interface AuditFilter { category: string; userId: string; from: string; to: string; q: string }

export const auditPath = (f: AuditFilter, cursor: Cursor, limit: number) => `/audit${query({ ...f, ...cursor, limit })}`;

/** The CSV of everything the filter matches (at most 50,000 newest rows); the download is logged too. */
export const downloadAudit = (f: AuditFilter) => fetchFile(`/audit/export.csv${query({ ...f })}`);
